// =============================================================================
// MeuDriver — Hook: useLoyalty
// Saldo de pontos de fidelidade e histórico de transações
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import type { LoyaltyTransactionRow } from '@/types/database'

export function useLoyaltyPoints() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['loyalty-points', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('loyalty_points')
        .eq('id', user!.id)
        .single()
      if (error) throw error
      return (data as { loyalty_points: number }).loyalty_points ?? 0
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60,
  })
}

export function useLoyaltyHistory() {
  const { user } = useAuth()

  return useQuery<LoyaltyTransactionRow[]>({
    queryKey: ['loyalty-history', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('loyalty_transactions')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return (data ?? []) as LoyaltyTransactionRow[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60,
  })
}

export function useRedeemLoyaltyPoints() {
  const qc = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ bookingId, points }: { bookingId: string; points: number }) => {
      const { data, error } = await supabase.rpc('redeem_loyalty_points', {
        p_booking_id: bookingId,
        p_points: points,
      })
      if (error) throw error
      return data as number // desconto em R$
    },
    onSuccess: (discount) => {
      qc.invalidateQueries({ queryKey: ['loyalty-points', user?.id] })
      qc.invalidateQueries({ queryKey: ['loyalty-history', user?.id] })
      toast.success(`Pontos resgatados! Desconto de R$ ${discount.toFixed(2)} aplicado.`)
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erro ao resgatar pontos.')
    },
  })
}
